<script setup>
import { computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { fromUnixTime, isToday, isYesterday } from 'date-fns';
import { useMapGetter } from 'dashboard/composables/store';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import MessageList from 'dashboard/components-next/message/MessageList.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  // Store conversation records (push_data shape) ordered oldest-first.
  conversations: { type: Array, default: () => [] },
  currentUserId: { type: Number, default: 0 },
  accountId: { type: [String, Number], required: true },
  // (id) => total message count for the conversation, from the endpoint.
  messagesCount: { type: Function, default: () => 0 },
  // (id) => whether earlier messages are currently loading for a conversation.
  isLoadingEarlier: { type: Function, default: () => false },
  // Scroll container element used as the intersection-observer root.
  scrollRoot: { type: Object, default: null },
});

const emit = defineEmits(['loadEarlier']);

const { t } = useI18n();
const inboxGetter = useMapGetter('inboxes/getInbox');

// Auto-load a conversation's earlier messages when its trigger scrolls into
// view, so the thread pages in history as the user scrolls up (like a chat).
const sentinels = new Map();
let observer = null;

const onIntersect = entries => {
  entries.forEach(entry => {
    if (!entry.isIntersecting) return;
    const id = Number(entry.target.dataset.conversationId);
    const conversation = props.conversations.find(c => c.id === id);
    if (conversation) emit('loadEarlier', conversation);
  });
};

const ensureObserver = () => {
  if (observer || !props.scrollRoot) return;
  observer = new IntersectionObserver(onIntersect, {
    root: props.scrollRoot,
    rootMargin: '150px 0px 0px 0px',
  });
  sentinels.forEach(el => observer.observe(el));
};

const setSentinel = (id, el) => {
  const existing = sentinels.get(id);
  if (existing && observer) observer.unobserve(existing);
  if (el) {
    sentinels.set(id, el);
    ensureObserver();
    observer?.observe(el);
  } else {
    sentinels.delete(id);
  }
};

watch(() => props.scrollRoot, ensureObserver);
onBeforeUnmount(() => observer?.disconnect());

const inboxName = conversation =>
  inboxGetter.value(conversation.inbox_id)?.name || '';
const isEmailChannel = conversation =>
  conversation.meta?.channel === 'Channel::Email';

const formatDate = timestamp =>
  messageTimestamp(timestamp, 'MMM d, yyyy · h:mm a');

const dayLabel = timestamp => {
  const date = fromUnixTime(timestamp);
  if (isToday(date)) return t('CONTACT_INTERACTIONS.THREAD.TODAY');
  if (isYesterday(date)) return t('CONTACT_INTERACTIONS.THREAD.YESTERDAY');
  return messageTimestamp(timestamp, 'MMM d, yyyy');
};

// Split a conversation's messages into contiguous per-day buckets so we can
// render a date separator between days, like the main conversation view.
const buildDayGroups = conversation => {
  const groups = [];
  let currentKey = null;
  (conversation.messages || []).forEach(message => {
    const key = fromUnixTime(message.created_at).toDateString();
    if (key !== currentKey) {
      currentKey = key;
      groups.push({
        key: `${conversation.id}-${key}`,
        label: dayLabel(message.created_at),
        messages: [],
      });
    }
    groups[groups.length - 1].messages.push(message);
  });
  return groups;
};

const groupedConversations = computed(() =>
  props.conversations.map(conversation => ({
    conversation,
    dayGroups: buildDayGroups(conversation),
  }))
);

const STATUS_META = {
  resolved: {
    icon: 'i-lucide-check-circle',
    color: 'text-n-teal-10',
    labelKey: 'CONTACT_INTERACTIONS.THREAD.CONVERSATION_RESOLVED',
  },
  pending: {
    icon: 'i-lucide-clock',
    color: 'text-n-blue-10',
    labelKey: 'CONTACT_INTERACTIONS.THREAD.STATUS_PENDING',
  },
  snoozed: {
    icon: 'i-lucide-alarm-clock',
    color: 'text-n-amber-10',
    labelKey: 'CONTACT_INTERACTIONS.THREAD.STATUS_SNOOZED',
  },
  open: {
    icon: 'i-lucide-circle-dot',
    color: 'text-n-amber-10',
    labelKey: 'CONTACT_INTERACTIONS.THREAD.STATUS_OPEN',
  },
};

const statusMeta = conversation =>
  STATUS_META[conversation.status] || STATUS_META.open;

const endLabel = conversation =>
  t(statusMeta(conversation).labelKey, {
    date: formatDate(conversation.last_activity_at),
  });

const hiddenMessageCount = conversation =>
  Math.max(
    0,
    props.messagesCount(conversation.id) - (conversation.messages?.length || 0)
  );

const conversationPath = conversationId =>
  frontendURL(
    conversationUrl({ accountId: props.accountId, id: conversationId })
  );
</script>

<template>
  <div class="flex flex-col pb-6">
    <section
      v-for="{ conversation, dayGroups } in groupedConversations"
      :key="conversation.id"
      class="flex flex-col"
    >
      <!-- Conversation start delimiter -->
      <div class="flex items-center gap-3 px-4 pt-6 pb-2 md:px-6">
        <span class="h-px flex-grow bg-n-strong" />
        <span
          class="flex items-center gap-1.5 rounded-full bg-n-alpha-black2 px-3 py-1 text-xs font-medium text-n-slate-11 whitespace-nowrap"
        >
          <span class="i-lucide-play size-3 text-n-teal-10" />
          {{
            t('CONTACT_INTERACTIONS.THREAD.CONVERSATION_STARTED', {
              id: conversation.id,
              date: formatDate(conversation.created_at),
            })
          }}
          <template v-if="inboxName(conversation)">
            <span class="text-n-slate-8">·</span>
            <span class="text-n-slate-10">{{ inboxName(conversation) }}</span>
          </template>
        </span>
        <span class="h-px flex-grow bg-n-strong" />
      </div>

      <!-- Earlier messages auto-load when this row scrolls into view; the
           button is a manual fallback (e.g. when the thread does not scroll). -->
      <div
        v-if="hiddenMessageCount(conversation) > 0"
        :ref="el => setSentinel(conversation.id, el)"
        :data-conversation-id="conversation.id"
        class="flex items-center justify-center py-1"
      >
        <Spinner
          v-if="isLoadingEarlier(conversation.id)"
          class="text-n-slate-10"
        />
        <button
          v-else
          type="button"
          class="text-xs font-medium text-n-blue-11 hover:underline"
          @click="emit('loadEarlier', conversation)"
        >
          {{
            t('CONTACT_INTERACTIONS.THREAD.LOAD_EARLIER_MESSAGES', {
              count: hiddenMessageCount(conversation),
            })
          }}
        </button>
      </div>

      <template v-for="group in dayGroups" :key="group.key">
        <!-- Day separator -->
        <div class="flex items-center justify-center py-2">
          <span
            class="rounded-full bg-n-alpha-black1 px-2.5 py-0.5 text-xs font-medium text-n-slate-10"
          >
            {{ group.label }}
          </span>
        </div>
        <MessageList
          :messages="group.messages"
          :current-user-id="currentUserId"
          :is-an-email-channel="isEmailChannel(conversation)"
        />
      </template>

      <!-- Conversation end delimiter -->
      <div class="flex items-center gap-3 px-4 pt-2 pb-2 md:px-6">
        <span class="h-px flex-grow bg-n-strong" />
        <span
          class="flex items-center gap-1.5 rounded-full bg-n-alpha-black2 px-3 py-1 text-xs font-medium text-n-slate-11 whitespace-nowrap"
        >
          <span
            class="size-3"
            :class="[
              statusMeta(conversation).icon,
              statusMeta(conversation).color,
            ]"
          />
          {{ endLabel(conversation) }}
          <template v-if="conversation.meta?.assignee">
            <span class="text-n-slate-8">·</span>
            <span class="text-n-slate-10">
              {{
                t('CONTACT_INTERACTIONS.THREAD.ASSIGNED_TO', {
                  name: conversation.meta.assignee.name,
                })
              }}
            </span>
          </template>
          <router-link
            :to="conversationPath(conversation.id)"
            class="ml-1 font-medium text-n-blue-11 hover:underline"
          >
            {{ t('CONTACT_INTERACTIONS.THREAD.OPEN') }}
          </router-link>
        </span>
        <span class="h-px flex-grow bg-n-strong" />
      </div>
    </section>
  </div>
</template>
